import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../primitives';
import CreateActionControls from '../create/CreateActionControls';
import { usePromptHelperMutation } from '../../core/queries/mutations';
import { useAuthStore } from '../../store/authStore';
import ReferenceUploadFlow from './ReferenceUploadFlow';
import useConsentGuard from '../../hooks/useConsentGuard';
import { getPhotoCreditCost } from '../../core/generationPricing';
import BottomSheet from '../BottomSheet';

function pluralCreditsKey(count) {
  const mod100 = Math.abs(count) % 100;
  const mod10 = Math.abs(count) % 10;
  if (mod100 >= 11 && mod100 <= 14) return 'credits5';
  if (mod10 === 1) return 'credit';
  if (mod10 >= 2 && mod10 <= 4) return 'credits234';
  return 'credits5';
}

export default function PhotoPickSheet({
  open = false,
  testId = 'photo-pick-sheet',
  preset,
  references = [],
  requiredCount = 1,
  loading = false,
  error,
  onClose,
  onReferencesChange,
  onCreate,
}) {
  const { t, i18n } = useTranslation();
  const { requireConsent } = useConsentGuard();
  const credits = useAuthStore((state) => state.credits);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [promptOpen, setPromptOpen] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [visualBatchCount, setVisualBatchCount] = useState(1);
  const [aiUnavailable, setAiUnavailable] = useState(false);
  const [aiError, setAiError] = useState('');
  const [activeSlot, setActiveSlot] = useState(null);
  const promptHelperMutation = usePromptHelperMutation();
  const aiLoading = promptHelperMutation.isPending;
  const busy = loading || uploading;
  const isOpen = Boolean(open || preset);

  const isPair = preset ? requiredCount > 1 : false;
  const ready = Array.from({ length: requiredCount })
    .every((_, index) => Boolean(references[index]?.url));
  const singleReference = !isPair ? references[0] : null;
  const totalCredits = getPhotoCreditCost(preset?.credits, '1.5K') * visualBatchCount;
  const totalCreditsWord = t(`prompt.${pluralCreditsKey(totalCredits)}`);
  const balanceValue = Number(credits?.total ?? 0) || 0;
  const title = isPair
    ? t('photoCatalog.pickTwoSelfies', 'Pick 2 Selfies')
    : t('videoGenerate.pickPhoto', 'Pick A Photo');
  const hint = isPair
    ? t(
        'photoCatalog.pickTwoHint',
        'Please upload two high quality photos where a face is clearly visible.'
      )
    : t(
        'videoGenerate.pickPhotoHint',
        'Upload a high quality photo for better results'
      );

  useEffect(() => {
    if (!ready) {
      setPromptOpen(false);
      setCustomPrompt('');
      setAiUnavailable(false);
      setAiError('');
    }
  }, [ready, singleReference]);

  useEffect(() => {
    if (preset) {
      setVisualBatchCount(1);
      setUploadError(null);
      setActiveSlot(null);
    }
  }, [preset]);

  const activeSlotLabel = isPair && activeSlot !== null
    ? t('photoCatalog.photoSlot', 'Photo {{number}}', { number: activeSlot + 1 })
    : null;

  const handleUploadingChange = useCallback((nextUploading) => {
    setUploading(nextUploading);
  }, []);

  const performGeneratePrompt = async () => {
    if (!preset || aiLoading || aiUnavailable) return;

    try {
      setAiError('');
      const res = await promptHelperMutation.mutateAsync({
        userText: '',
        presetId: preset.id,
        presetContext: {
          name: preset.name || preset.title || '',
          nameEn: preset.nameEn || preset.name_en || '',
          systemPrompt: preset.systemPrompt || '',
          promptVariants: preset.promptVariants || null,
        },
        language: i18n.language?.startsWith('en') ? 'en' : 'ru',
      });
      const generated = typeof res?.prompt === 'string' ? res.prompt.trim() : '';
      if (generated) {
        setCustomPrompt(generated.slice(0, 1000));
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 503) {
        setAiUnavailable(true);
        setAiError(t('prompt.aiUnavailable', 'Сейчас недоступно'));
      } else if (status === 429) {
        setAiError(t('prompt.aiRateLimited', 'Слишком много запросов, повторите позже'));
      } else {
        setAiError(t('prompt.aiError', 'Не удалось сгенерировать. Попробуйте позже'));
      }
    }
  };

  const handleGeneratePrompt = () => {
    requireConsent(performGeneratePrompt, 'prompt_helper');
  };

  if (!isOpen) return null;

  return (
    <BottomSheet
      open={isOpen}
      busy={busy}
      title={activeSlotLabel || title}
      subtitle={hint}
      onClose={onClose}
      onBack={activeSlotLabel ? () => setActiveSlot(null) : undefined}
      testId={testId}
    >
      <>
            <ReferenceUploadFlow
              title={title}
              hint={hint}
              references={references}
              requiredCount={requiredCount}
              disabled={loading}
              onReferencesChange={onReferencesChange}
              onUploadingChange={handleUploadingChange}
              onError={setUploadError}
              cameraLabel={t('videoGenerate.camera', 'Camera')}
              galleryLabel={t('videoGenerate.gallery', 'Gallery')}
              removeLabel={t('common.delete')}
              showHeader={false}
              activeSlot={activeSlot}
              onActiveSlotChange={setActiveSlot}
            />

            {!isPair ? (
              <>
                <div
                  className={[
                    'overflow-hidden transition-all duration-200 ease-out',
                    ready ? 'max-h-[360px] translate-y-0 opacity-100' : 'max-h-0 translate-y-3 opacity-0',
                  ].join(' ')}
                  aria-hidden={!ready}
                  data-testid="photo-progressive-controls"
                >
              <button
                type="button"
                role="switch"
                aria-checked={promptOpen}
                onClick={() => setPromptOpen((value) => !value)}
                className="mb-3 flex w-full items-center justify-between rounded-2xl border border-white/[0.08] bg-white/[0.06] px-4 py-3 text-left transition-colors duration-200"
                data-testid="photo-prompt-toggle"
              >
                <span className="font-sans text-[15px] font-semibold leading-none text-text-primary">
                  {t('photoCatalog.extendPromptQuestion', 'Дополнить промпт?')}
                </span>
                <span
                  className={[
                    'relative h-7 w-12 rounded-full transition-colors duration-200',
                    promptOpen ? 'bg-[#FFAC0B]' : 'bg-white/[0.14]',
                  ].join(' ')}
                >
                  <span
                    className={[
                      'absolute top-1 h-5 w-5 rounded-full bg-white transition-transform duration-200',
                      promptOpen ? 'translate-x-6' : 'translate-x-1',
                    ].join(' ')}
                  />
                </span>
              </button>

              <div
                className={[
                  'overflow-hidden transition-all duration-200 ease-out',
                  promptOpen ? 'mb-4 max-h-[178px] translate-y-0 opacity-100' : 'max-h-0 translate-y-2 opacity-0',
                ].join(' ')}
                data-testid="photo-prompt-panel"
              >
                <div
                  className={[
                    'relative min-h-[164px] rounded-2xl border bg-white/[0.07] p-4',
                    aiError ? 'border-rose-500/60' : 'border-white/[0.08]',
                  ].join(' ')}
                >
                  <textarea
                    value={customPrompt}
                    onChange={(event) => setCustomPrompt(event.target.value.slice(0, 1000))}
                    placeholder={t(
                      'photoCatalog.customPromptPlaceholder',
                      'Дополните промт своим описанием'
                    )}
                    maxLength={1000}
                    className="h-[106px] w-full resize-none bg-transparent pr-1 font-sans text-sm font-semibold leading-5 text-text-primary placeholder:text-text-secondary focus:outline-none"
                    data-testid="photo-custom-prompt-input"
                  />
                  <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={handleGeneratePrompt}
                      disabled={aiLoading || aiUnavailable || uploading}
                      className={[
                        'inline-flex h-8 items-center gap-2 rounded-full px-3 font-sans text-[11px] font-bold',
                        aiUnavailable || uploading
                          ? 'bg-white/[0.04] text-text-secondary/60 cursor-not-allowed'
                          : 'bg-white/[0.08] text-text-secondary',
                        aiLoading ? 'opacity-60' : '',
                      ].join(' ')}
                      data-testid="photo-generate-prompt-button"
                    >
                      {/* TODO: replace temporary sparkle icon with owner-provided SVG assets for modal actions. */}
                      <Icon name="small-sparkle" size={13} className="text-text-primary" />
                      {aiUnavailable
                        ? t('prompt.aiUnavailable', 'Сейчас недоступно')
                        : t('prompt.generatePrompt', 'Сгенерировать промт')}
                    </button>
                    <span className="font-sans text-sm font-semibold text-text-secondary">
                      {customPrompt.length} / 1000
                    </span>
                  </div>
                </div>
                {aiError ? (
                  <p className="mt-2 text-left font-sans text-xs font-semibold text-rose-400">
                    {aiError}
                  </p>
                ) : null}
              </div>

              {ready ? (
                <CreateActionControls
                  className="mb-5"
                  batchCount={visualBatchCount}
                  totalCredits={totalCredits}
                  totalCreditsWord={totalCreditsWord}
                  balanceValue={balanceValue}
                  createLabel={t('prompt.create', 'Create')}
                  balanceLabel={t('prompt.balance', 'Balance')}
                  generatingLabel={t('videoGenerate.creating', 'Creating...')}
                  loading={busy}
                  disabledVariant="gold"
                  onCreate={() => requireConsent(
                    () => onCreate({
                      prompt: customPrompt.trim(),
                      batchCount: visualBatchCount,
                    }),
                    'generate',
                  )}
                  onDecrease={() => setVisualBatchCount((count) => Math.max(1, count - 1))}
                  onIncrease={() => setVisualBatchCount((count) => Math.min(4, count + 1))}
                  counterTestId="photo-batch-counter"
                  buttonTestId="photo-create-button"
                />
              ) : null}
                </div>
              </>
            ) : null}

            {(error || uploadError) && (
              <p className="mb-3 text-center font-sans text-[13px] font-semibold text-red-500">
                {error || uploadError}
              </p>
            )}

            {!isPair && !ready ? (
              <CreateActionControls
                batchCount={visualBatchCount}
                totalCredits={totalCredits}
                totalCreditsWord={totalCreditsWord}
                balanceValue={balanceValue}
                createLabel={t('prompt.create', 'Create')}
                balanceLabel={t('prompt.balance', 'Balance')}
                generatingLabel={t('videoGenerate.creating', 'Creating...')}
                disabled
                disabledVariant="gold"
                showCounter={false}
                onCreate={() => {}}
                onDecrease={() => {}}
                onIncrease={() => {}}
                buttonTestId="photo-create-button"
              />
            ) : null}

            {isPair ? (
              <button
                type="button"
                disabled={!ready || busy}
                onClick={() => requireConsent(
                  () => onCreate({
                    prompt: customPrompt.trim(),
                    batchCount: visualBatchCount,
                  }),
                  'generate',
                )}
                className={[
                  'flex h-[60px] w-full items-center justify-center rounded-2xl',
                  'font-sans text-[15px] font-bold leading-none',
                  'transition-transform active:scale-[0.98]',
                  (!ready || busy) ? 'bg-white/10 text-white/40' : 'text-[#010103] shadow-[0_10px_28px_rgba(255,144,18,0.18)]',
                ].join(' ')}
                style={
                  !ready || busy
                    ? undefined
                    : { background: 'linear-gradient(135deg, #FFC24C 0%, #FF9012 100%)' }
                }
                data-testid="photo-create-button"
              >
                {loading
                  ? t('videoGenerate.creating', 'Creating...')
                  : t('common.continue', 'Continue')}
              </button>
            ) : null}
      </>
    </BottomSheet>
  );
}
